import { computed, makeAutoObservable } from "mobx";
import { StudentStore } from "./StudentStore";
import {
  EmptyGroupResponseImpl,
  GroupWithStudentsAndModulesResponseApi,
} from "../../DTO/ServerResponseType/Group/Group";
import { StudentViewEnum } from "../../ProfileWindow/TutorWindows/GroupViewWindow/Enum/StudentViewEnum";
import { GroupLearningPathModuleFlagEnum } from "../../Enums/GroupLearningPathModuleFlagEnum";
import {
  EmptyStudentResponseImpl,
  StudentResponseApi,
} from "../../DTO/ServerResponseType/Student/Student";
import { StudentWithGroupsResponseApi } from "../../DTO/ServerResponseType/Student/StudentWithGroups";
import studentServerClient from "../../ServerClients/StudentServerClient";
import { AppStoreApi } from "../AppStore";
import { StudentsStore } from "./StudentsStore";
import studentProgressServerClient from "../../ServerClients/StudentProgressServerClient";
import { StudentEstimateStateEnum } from "../../ProfileWindow/TutorWindows/GroupViewWindow/Enum/StudentEstimateStateEnum";
import { ModuleResourceWithStudentProgressResponseApi } from "../../DTO/ServerResponseType/Group/ModuleResourceWithStudentProgress";
import { ModuleResponseApi } from "../../DTO/ServerResponseType/Module/Module";
import { AES } from "crypto-ts";
import groupServerClient from "../../ServerClients/GroupServerClient";

export class GroupWindowStore {
  root: AppStoreApi;
  addStudentStore: StudentStore = new StudentStore(this);
  studentsStore: StudentsStore = new StudentsStore(this);
  group: GroupWithStudentsAndModulesResponseApi = new EmptyGroupResponseImpl();
  tempStudents: StudentWithGroupsResponseApi[] = [];
  tempStudentView: StudentViewEnum = StudentViewEnum.StudentList;
  showAddedStudentCallout: boolean = false;
  showCreateAndAddedStudentCallout: boolean = false;
  showValidateGroupModules: boolean = true;
  groupModulesBunchSize: number = 9;
  numOfGroupModulesBunch: number = 0;
  deleteGroupStudentData!: {
    group: { groupId: string; groupName: string };
    student: { studentId: string; studentName: string };
  };
  showDeleteStudentModal: boolean = false;
  showStudentProgress: boolean = false;
  studentProgressToShow: {
    module: ModuleResponseApi & {
      moduleOrder: number;
      moduleResources: ModuleResourceWithStudentProgressResponseApi[];
    };
    studentId: string | undefined;
  } | null = null;

  constructor(root: AppStoreApi) {
    makeAutoObservable(this);
    this.root = root;
  }

  setGroup = (group: GroupWithStudentsAndModulesResponseApi) => {
    this.group = group;
    this.tempStudents = [];
  };

  onStudentListShowAndStudentProgressClose = () => {
    this.tempStudentView = StudentViewEnum.StudentList;
  };

  onStudentProgressShowAndStudentListClose = () => {
    this.tempStudentView = StudentViewEnum.StudentProgress;
  };

  onAddedStudentCalloutShow = () => {
    this.showAddedStudentCallout = true;
  };

  onAddedStudentCalloutClose = () => {
    this.showAddedStudentCallout = false;
    this.tempStudents = [];
  };

  onCreateAndAddedStudentCalloutShow = (studentData?: StudentResponseApi) => {
    this.showCreateAndAddedStudentCallout = true;
    this.addStudentStore.setStudent(
      studentData ?? new EmptyStudentResponseImpl()
    );
  };

  onCreateAndAddedStudentCalloutClose = () => {
    this.showCreateAndAddedStudentCallout = false;
  };

  async onChangeGroupModuleStudentFlagAsync(
    groupModuleId: string,
    doOpenModuleForStudent: boolean
  ): Promise<void> {
    await groupServerClient
      .sendToUpdateGroupModuleStudentFlagAsync(
        groupModuleId,
        doOpenModuleForStudent
      )
      .then(() => this.root.groupsStore.loadAllGroupsAsync());
  }

  async onChangeModuleEstimate(
    moduleResources: (ModuleResourceWithStudentProgressResponseApi & {
      estimate: StudentEstimateStateEnum;
    })[],
    studentId?: string
  ) {
    let promises: Promise<void>[] = [];

    moduleResources
      .filter(
        (moduleResource) =>
          moduleResource.estimate !== StudentEstimateStateEnum.None
      )
      .forEach((moduleResource) => {
        promises.push(
          this.onChangeEstimateAsync(
            moduleResource.estimate === StudentEstimateStateEnum.AllStudents,
            moduleResource.moduleResourceId,
            studentId
          )
        );
      });

    await Promise.all(promises).finally(() =>
      this.root.groupsStore.loadAllGroupsAsync()
    );
  }

  async onChangeEstimateAsync(
    estimate: boolean,
    moduleResourceId: string,
    studentId?: string
  ) {
    let cookiesWithSessionId = document.cookie.match("sessionId");
    let partsCookie = cookiesWithSessionId?.input?.split("=");
    let key =
      Array.isArray(partsCookie) && partsCookie.length >= 2
        ? partsCookie[1]
        : null;

    if (key === null) throw new Error("ключ не прочитан");

    let notNullKey: string = key;

    let createToken = (data: object, key: string) =>
      AES.encrypt(JSON.stringify(data), key).toString();

    let token = createToken(
      {
        moduleResourceId: moduleResourceId,
        studentId: studentId,
        groupId: this.group.groupId,
        estimate: estimate,
      },
      notNullKey
    );
    return await studentProgressServerClient.sendToEstimateStudentProgressAsync(
      token
    );
  }

  onStudentProgressDialogModalShow = (
    module: ModuleResponseApi & {
      moduleOrder: number;
      moduleResources: ModuleResourceWithStudentProgressResponseApi[];
    },
    studentId?: string
  ) => {
    this.showStudentProgress = true;

    // view module resources that a student can complete
    let moduleWithOnlyOrderedResources = {
      ...module,
    };

    moduleWithOnlyOrderedResources.moduleResources =
      module.moduleResources.filter(
        (moduleResource) => moduleResource.order !== null
      );

    this.studentProgressToShow = {
      module: moduleWithOnlyOrderedResources,
      studentId: studentId,
    };
  };

  onStudentProgressDialogModalClose = () => {
    this.showStudentProgress = false;
    this.studentProgressToShow = null;
  };

  onChangeShowValidateGroupModules = () => {
    this.showValidateGroupModules = !this.showValidateGroupModules;
  };

  @computed get canShowPreviousBunch() {
    return this.numOfGroupModulesBunch > 0;
  }

  @computed get canShowNextBunch() {
    return this.numOfGroupModulesBunch < this.maxNumOfGroupModulesBunch;
  }

  onChangeNumOfGroupModulesBunchToPreviousBunch = () => {
    if (this.canShowPreviousBunch) this.numOfGroupModulesBunch--;
  };

  onChangeNumOfGroupModulesBunchToNextBunch = () => {
    if (this.canShowNextBunch) this.numOfGroupModulesBunch++;
  };

  setTempStudents = (tempStudents: StudentWithGroupsResponseApi[]) => {
    this.tempStudents = tempStudents;
  };

  addStudentsToGroupAsync = async () => {
    return Promise.all(
      this.tempStudents.map(async (tempStudent) => {
        await studentServerClient.sendToAddStudentToGroupAsync(
          tempStudent.studentId,
          this.group.groupCode
        );
      })
    ).finally(() => {
      this.root.groupsStore.loadAllGroupsAsync();
    });
  };

  disconnectStudentFromGroupAsync = async (studentId: string) => {
    return await studentServerClient
      .sendToDisconnectStudentFromGroupAsync(studentId, this.group.groupId)
      .then(() => {
        this.root.groupsStore.loadAllGroupsAsync();
      });
  };

  @computed get maxNumOfGroupModulesBunch() {
    return Math.floor(
      this.groupModulesToShowDependOnShowValidateGroupModules.length /
        this.groupModulesBunchSize
    );
  }

  @computed get groupModulesToShowDependOnShowValidateGroupModules() {
    let groupModules = this.group.groupModules.filter((groupModule) =>
      this.showValidateGroupModules
        ? true
        : groupModule.groupLearningPathModuleFlag !==
          GroupLearningPathModuleFlagEnum.Validate
    );

    return groupModules;
  }

  @computed get groupModulesToShow() {
    let groupModules =
      this.groupModulesToShowDependOnShowValidateGroupModules.slice(
        this.numOfGroupModulesBunch * this.groupModulesBunchSize,
        this.numOfGroupModulesBunch * this.groupModulesBunchSize +
          this.groupModulesBunchSize
      );

    return groupModules;
  }

  onDeleteStudentModalOpen = (studentId: string) => {
    let student = this.group.groupStudents.find(
      ({ student }) => student.studentId === studentId
    )?.student;

    if (student === undefined) {
      alert("Error: student not found");
    } else {
      this.showDeleteStudentModal = true;
      this.deleteGroupStudentData = {
        group: this.group,
        student: student,
      };
    }
  };

  onDeleteStudentModalClose = () => {
    this.showDeleteStudentModal = false;
  };
}
